import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigationType, useSearchParams } from "react-router-dom";
import { LoadingCircle } from "../../components";
import { useSelector } from "react-redux";
import {
  FILTERS,
  PARAM_TO_FILTER,
  serializeFilters,
  getDisplayedProducts,
} from "../../utils/filtersUtils";
import {
  getInitialVisibleCount,
  getRestoredVisibleCount,
  persistShopViewState,
  ITEMS_INCREMENT_GRID,
  ITEMS_INCREMENT_LIST,
} from "../../utils/shopViewStateUtils";
import GridContent from "./GridContent";
import "./Shop.css";

const SCROLL_OBSERVER_MARGIN = "200px";

const Shop = ({ region, grid }) => {
  const [filter, setFilter] = useState("All");
  const products = useSelector((state) => state.store.store);
  const loading = useSelector((state) => state.store.loading);
  const hasProducts = Array.isArray(products) && products.length > 0;
  const showLoading = loading && !hasProducts;
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const sentinelRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(() =>
    getInitialVisibleCount(grid),
  );
  const searchQuery = searchParams.toString();

  useEffect(() => {
    const section = searchParams.get("section");
    const sectionFilter = section
      ? PARAM_TO_FILTER[section.toLowerCase()]
      : null;
    const nextFilter =
      sectionFilter && FILTERS.includes(sectionFilter) ? sectionFilter : "All";
    setFilter(nextFilter);
  }, [searchParams]);

  const handleSetFilter = (nextFilter) => {
    setFilter(nextFilter);
    setSearchParams(
      serializeFilters({
        categories: nextFilter === "All" ? ["All"] : [nextFilter],
      }),
    );
  };

  const handleBeforeProductNavigation = () => {
    persistShopViewState({
      visibleCount,
      search: searchQuery,
      grid,
    });
  };

  const displayedProducts = useMemo(
    () => getDisplayedProducts(products, filter, searchParams),
    [products, filter, searchQuery],
  );

  useEffect(() => {
    if (navigationType !== "POP") return;

    const savedVisibleCount = getRestoredVisibleCount({
      grid,
      searchQuery,
    });

    if (savedVisibleCount) {
      setVisibleCount(savedVisibleCount);
    }
  }, [grid, navigationType, searchQuery]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => {
            const increment = grid
              ? ITEMS_INCREMENT_GRID
              : ITEMS_INCREMENT_LIST;
            return Math.min(prev + increment, displayedProducts.length);
          });
        }
      },
      { rootMargin: SCROLL_OBSERVER_MARGIN, threshold: 0 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [grid, displayedProducts.length]);

  const visibleProducts = useMemo(
    () => displayedProducts.slice(0, visibleCount),
    [displayedProducts, visibleCount],
  );

  return (
    <div className="shop">
      <div className="content-padding">
        {showLoading ? (
          <LoadingCircle />
        ) : (
          <div>
            <div className={grid ? "shop_list_single" : "shop_list"}>
              {visibleProducts.map((product) => (
                <GridContent
                  key={product.id}
                  product={product}
                  onBeforeNavigate={handleBeforeProductNavigation}
                  grid={grid}
                />
              ))}
            </div>
            <div ref={sentinelRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
